import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Grid, InputAdornment } from '@material-ui/core';
import { FileCopy, OpenInNew } from '@material-ui/icons';
import {
  Button,
  IconButton,
  Switch,
  TextField,
  useDialogs,
  ValueAvatar,
} from '@michelin/acid-components';
import { GooglePlacesDetails } from 'components/Map/GooglePlacesDetails';
import { GlobalTextInputProps } from '../../Types';
import {
  getGooglePlacesInfo,
  GooglePlacesInfo,
  GooglePlacesMapInfo,
} from '../../Types/GooglePlaces';
import { Map } from '../Map';

export type OnChangeIds = 'google_places_id'
| 'service_address_latitude'
| 'service_address_longitude'
| 'requested_geo_code_update'
| 'requested_geo_code_latitude'
| 'requested_geo_code_longitude'
| 'requested_geo_code_explanation';

interface GooglePlacesProps {
  customer_name: string,
  googlePlacesInfo?: GooglePlacesInfo,
  googlePlacesInfoOnChange?: (newGooglePlacesInfo: GooglePlacesInfo) => void,
  googlePlacesMapInfo: GooglePlacesMapInfo,
  onChange: (
    id: OnChangeIds,
    value: any,
    info: GooglePlacesMapInfo) => void,
  errors: any,
  readOnly: boolean,
  inputProps: GlobalTextInputProps,
  acceptLocationShow: boolean,
  acceptLocationOnSave: (geoData: any) => void,
}

export async function loadGooglePlacesInfo(googlePlacesId: string): Promise<GooglePlacesInfo | null> {
  if (!googlePlacesId || googlePlacesId.length < 20) {
    return null;
  }

  try {
    const gpi = await getGooglePlacesInfo(googlePlacesId);
    if (gpi === null) {
      return null;
    }
    return gpi;
  } catch (e) {
    return null;
  }
}

export default function GooglePlaces(props: GooglePlacesProps) {
  const { t } = useTranslation();
  const { alertDialog, messageDialog } = useDialogs();
  const [displayGooglePlaces, setDisplayGooglePlaces] = useState(false);
  const [googlePlacesInfo, setGooglePlacesInfo] = useState(props.googlePlacesInfo);
  const value = { ...props.googlePlacesMapInfo };

  const copyLocationFromGoogle = async () => {
    if (!googlePlacesInfo) {
      const gpi = await loadGooglePlacesInfo(props.googlePlacesMapInfo.google_places_id || '');
      if (!gpi) {
        alertDialog(
          t('Please check your google places ID or try again later...').split('\n'),
          t('Error obtaining location form Google Places'),
          t('Ok'),
        );
        return;
      }
      setGooglePlacesInfo(gpi);
      if (props.googlePlacesInfoOnChange) props.googlePlacesInfoOnChange(gpi);
    }

    if (!props.onChange || !googlePlacesInfo) return;

    value.requested_geo_code_latitude = googlePlacesInfo.geometry.location.lat.toString();
    value.requested_geo_code_longitude = googlePlacesInfo.geometry.location.lng.toString();

    props.onChange('requested_geo_code_latitude', googlePlacesInfo.geometry.location.lat.toString(), value);
    props.onChange('requested_geo_code_longitude', googlePlacesInfo.geometry.location.lng.toString(), value);
  };

  return (
    <Grid container spacing={2}>
      <Grid item md={12} xs={12}>
        <TextField
          id="google_places_id"
          label={t('Google Places ID')}
          variant={props.inputProps.variant}
          style={props.inputProps.style}
          onChange={(e) => {
            value.google_places_id = e.target.value;
            props.onChange('google_places_id', e.target.value, value);
          }}
          error={props.errors.google_places_id}
          helperText={t('If provided we will display google reviews and photos on dealer & service partner location') as string}
          value={props.googlePlacesMapInfo.google_places_id || ''}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label={t('Display google places information')}
                  onClick={() => setDisplayGooglePlaces(true)}
                  disabled={!props.googlePlacesMapInfo.google_places_id}
                >
                  <OpenInNew />
                </IconButton>
              </InputAdornment>
            ),
            readOnly: props.readOnly,
          }}
        />
      </Grid>
      <Grid item md={12} xs={12}>
        <Map
          id="myMap"
          label={props.customer_name || ''}
          style={{ height: props.googlePlacesMapInfo.requested_geo_code_update ? 290 : 350 }}
          options={{
            zoom: 16,
          }}
          fixedMarker={{
            lat: parseFloat((props.googlePlacesMapInfo.geo_code_latitude || '0').trim()),
            lng: parseFloat((props.googlePlacesMapInfo.geo_code_longitude || '0').trim()),
          }}
          modifiedMarker={props.googlePlacesMapInfo.requested_geo_code_update ? {
            lat: parseFloat((props.googlePlacesMapInfo.requested_geo_code_latitude || '0').trim()),
            lng: parseFloat((props.googlePlacesMapInfo.requested_geo_code_longitude || '0').trim()),
          } : undefined}
          onDragend={(marker) => {
            if (!props.onChange) return;

            value.requested_geo_code_latitude = marker.lat.toString();
            value.requested_geo_code_longitude = marker.lng.toString();

            props.onChange('requested_geo_code_latitude', marker.lat.toString(), value);
            props.onChange('requested_geo_code_longitude', marker.lng.toString(), value);
          }}
          readOnly={props.readOnly}
        />
      </Grid>
      <Grid item md={6} xs={6}>
        <TextField
          variant={props.inputProps.variant}
          style={props.inputProps.style}
          id="requested_geo_code_latitude"
          label={t('Latitude')}
          type="number"
          readOnly
          value={props.googlePlacesMapInfo.requested_geo_code_update
            ? props.googlePlacesMapInfo.requested_geo_code_latitude || ''
            : props.googlePlacesMapInfo.geo_code_latitude || ''}
        />
      </Grid>
      <Grid item md={6} xs={6}>
        <TextField
          variant={props.inputProps.variant}
          style={props.inputProps.style}
          id="requested_geo_code_longitude"
          label={t('Longitude')}
          type="number"
          readOnly
          value={props.googlePlacesMapInfo.requested_geo_code_update
            ? props.googlePlacesMapInfo.requested_geo_code_longitude || ''
            : props.googlePlacesMapInfo.geo_code_longitude || ''}
        />
      </Grid>
      <Grid item md={10} xs={10}>
        <Switch
          id="requested_geo_code_update"
          label={t('Request location change')}
          labelPosition="right"
          uncheckedLabel={t('No')}
          checkedLabel={t('Yes')}
          checked={props.googlePlacesMapInfo.requested_geo_code_update}
          readOnly={props.readOnly}
          onChange={(e, v) => {
            if (props.readOnly) return;
            value.requested_geo_code_update = v;
            props.onChange('requested_geo_code_update', v, value);
            if (!v) return;
            const message = 'Please use the RED Pin to indicate the right coordinates\n'
              + 'for your location, and let us know why is it wrong filling\n'
              + 'the details on the text input below the map.';
            messageDialog(
              t(message).split('\n'),
              t("Let's fix your GPS location..."),
              t('Ok'),
              () => { },
              /*
              validateData({
                id: 'requested_geo_code_explanation',
                value: (commercial || {} as CommercialLocation).requested_geo_code_explanation,
              })
              */
            );
          }}
          color="primary"
        />
      </Grid>
      <Grid item md={2} xs={2}>
        {(!props.readOnly && props.googlePlacesMapInfo.requested_geo_code_update)
          ? (
            <div onClick={() => copyLocationFromGoogle()}>
              <ValueAvatar
                color="primary"
                icon={<FileCopy />}
                value=""
                tooltip={t('Copy from Google')}
              />
            </div>
          ) : null}
      </Grid>
      <Grid item md={12} xs={12}>
        {props.googlePlacesMapInfo.requested_geo_code_update ? (
          <TextField
            variant={props.inputProps.variant}
            InputProps={props.inputProps.InputProps}
            style={props.inputProps.style}
            onChange={(e) => {
              value.requested_geo_code_explanation = e.target.value;
              props.onChange('requested_geo_code_explanation', e.target.value, value);
            }}
            id="requested_geo_code_explanation"
            error={!props.readOnly && !props.googlePlacesMapInfo.requested_geo_code_explanation}
            label={t("Please let us know what's wrong with your location...")}
            value={props.googlePlacesMapInfo.requested_geo_code_explanation || ''}
            required
            multiline
            rowsMax="4"
            disabled={props.readOnly}
          />
        ) : null}
      </Grid>
      {props.readOnly && props.googlePlacesMapInfo.requested_geo_code_update && props.acceptLocationShow ? (
        <Grid item md={12} xs={12} style={{ textAlign: 'center' }}>
          <Button
            variant="outlined"
            size="small"
            color="primary"
            onClick={() => {
              const geoData = {
                approve: true,
                geo: {
                  geo_code_latitude: props.googlePlacesMapInfo.requested_geo_code_latitude,
                  geo_code_longitude: props.googlePlacesMapInfo.requested_geo_code_longitude,
                },
              };
              props.acceptLocationOnSave(geoData);
            }}
          >
            {t('Accept location change')}
          </Button>
        </Grid>
      ) : null}
      {
        displayGooglePlaces
          ? <GooglePlacesDetails placeId={props.googlePlacesMapInfo.google_places_id || ''} onClose={() => setDisplayGooglePlaces(false)} />
          : null
      }
    </Grid>
  );
}
